import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { toast } from 'sonner';
import { Button, Card, CardBody, EmptyState, Input, PageHeader, Pill } from '@/shared/components';
import { toApiError, useApi } from '@/shared/api';

export function SmartLockMappingPage() {
  const api = useApi();
  const queryClient = useQueryClient();
  const [codes, setCodes] = useState<Record<string, string>>({});
  const properties = useQuery({
    queryKey: ['properties', 'smart-lock'],
    queryFn: () => api.properties.list({ pageSize: 200 }),
  });
  const mappings = useQuery({
    queryKey: ['smart-lock', 'mappings', properties.data?.items.map((item) => item.id)],
    queryFn: async () =>
      Promise.all(
        (properties.data?.items ?? []).map((item) => api.properties.accessMapping.get(item.id)),
      ),
    enabled: Boolean(properties.data),
  });
  const save = useMutation({
    mutationFn: ({ propertyId, code }: { propertyId: string; code: string }) =>
      api.properties.accessMapping.setStaticCode(propertyId, { staticAccessCode: code }),
    onSuccess: async (_, variables) => {
      setCodes((current) => ({ ...current, [variables.propertyId]: '' }));
      await queryClient.invalidateQueries({ queryKey: ['smart-lock', 'mappings'] });
      toast.success('Zugangscode gespeichert');
    },
    onError: async (error) => {
      const apiError = await toApiError(error);
      toast.error(apiError.detail ?? apiError.title);
    },
  });
  const remove = useMutation({
    mutationFn: (propertyId: string) => api.properties.accessMapping.remove(propertyId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['smart-lock', 'mappings'] });
      toast.success('Zugangscode entfernt');
    },
    onError: async (error) => {
      const apiError = await toApiError(error);
      toast.error(apiError.detail ?? apiError.title);
    },
  });
  const mapped = new Map((mappings.data ?? []).map((mapping) => [mapping.propertyId, mapping]));

  return (
    <>
      <PageHeader
        eyebrow="Smart Lock"
        title="Zugangs"
        titleAccent="mapping"
        description="Statische Codes werden verschlüsselt gespeichert und Gästen erst zwölf Stunden vor Check-in angezeigt."
      />
      {properties.isLoading ? (
        <p className="text-ink-soft">Apartments werden geladen…</p>
      ) : properties.data?.items.length === 0 ? (
        <EmptyState
          icon="Building"
          title="Keine Apartments"
          description="Lege zuerst ein Apartment an, um einen Zugangscode zu hinterlegen."
        />
      ) : (
        <div className="space-y-3">
          {(properties.data?.items ?? []).map((property) => {
            const mapping = mapped.get(property.id);
            const value = codes[property.id] ?? '';
            return (
              <Card key={property.id}>
                <CardBody className="flex flex-col gap-4 p-5 md:flex-row md:items-center">
                  <div className="min-w-48 flex-1">
                    <h2 className="text-ink font-serif text-lg">{property.name}</h2>
                    <p className="text-ink-soft text-sm">{property.address.city}</p>
                  </div>
                  <Pill variant={mapping?.hasStaticAccessCode ? 'accent' : 'outline'}>
                    {mapping?.hasStaticAccessCode
                      ? 'Statischer Code hinterlegt'
                      : 'Kein Smart Lock'}
                  </Pill>
                  <Input
                    aria-label={`Zugangscode für ${property.name}`}
                    type="password"
                    autoComplete="new-password"
                    placeholder={
                      mapping?.hasStaticAccessCode
                        ? 'Neuen Code setzen'
                        : 'Statischen Code eingeben'
                    }
                    value={value}
                    onChange={(event) =>
                      setCodes((current) => ({ ...current, [property.id]: event.target.value }))
                    }
                    className="md:w-56"
                  />
                  <Button
                    variant="brand"
                    loading={save.isPending && save.variables?.propertyId === property.id}
                    disabled={!value.trim()}
                    onClick={() => save.mutate({ propertyId: property.id, code: value })}
                  >
                    Speichern
                  </Button>
                  {mapping?.hasStaticAccessCode && (
                    <Button
                      variant="ghost"
                      loading={remove.isPending && remove.variables === property.id}
                      onClick={() => remove.mutate(property.id)}
                    >
                      Entfernen
                    </Button>
                  )}
                </CardBody>
              </Card>
            );
          })}
        </div>
      )}
    </>
  );
}
